// React and related imports
import React from 'react';

// RTK Query Hooks
import { useFetchActiveContentQuery } from '../redux/api/contentApi';

// i18n hooks
import { useTranslation } from 'react-i18next';

// Custom Components
import { GroupedContentAccordion } from '../components/GroupedContentAccordion/GroupedContentAccordion';
import { PageHeader } from '../components/Shared/PageHeader';
import { ContentModal } from '../components/ContentModal/ContentModal';
import { CustomPagination } from '../components/Shared/CustomPagination/CustomPagination';
import { Loading } from '../components/Shared/Loading/Loading';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import { EmptyState } from '../components/Shared/EmptyState/EmptyState';
import { usePagination } from '../hooks/usePagination';

// Hooks
import { usePageTitle } from '../hooks/usePageTitle';

export const ContentPage: React.FC = () => {
  const { t } = useTranslation();

  usePageTitle(t('pageTitles.content'));

  const { currentPage, setCurrentPage, size } = usePagination(5);

  const { data, error, isLoading, refetch } = useFetchActiveContentQuery({
    page: currentPage,
    size,
  });

  const sortedData = React.useMemo(() => {
    const contentData = data?.data || [];
    return contentData
      .slice()
      .sort(
        (a, b) =>
          (b.contents?.length > 0 ? 1 : 0) - (a.contents?.length > 0 ? 1 : 0)
      );
  }, [data?.data]);

  if (isLoading) return <Loading />;

  if (error) return <ErrorComponent error={error} onRetry={refetch} />;

  if (!data?.data) {
    return <EmptyState />;
  }

  const maxTotalContentNumber = Math.max(
    ...data.data.map((group) => group.totalContents || 0)
  );

  const totalPages = Math.ceil(maxTotalContentNumber / size);

  return (
    <>
      <div className="mb-8">
        <PageHeader title={t('content.contentManagement')} />
        <CustomPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
      <div className="my-4 overflow-y-auto">
        {sortedData.map((item, index) => (
          <GroupedContentAccordion
            key={item._id}
            groupedContents={item}
            index={index}
          />
        ))}
      </div>
      <ContentModal />
    </>
  );
};
